import { TableSharedCssClassName } from '@atlaskit/editor-common/styles';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import type { DeletedDiffWidget } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { findDomRefAtPos, findParentNodeClosestToPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { InvokedFrom } from '../../utils/analytics/analyticsFlowTypes';
import { isNewAifcExperienceEnabledNoExposure } from '../../utils/new-aifc-experience-enabled';

/** Width (rem) removed from the AI modal in the New AIFC Editor Experience. */
export const MODAL_WIDTH_REDUCTION_REM: number = 10;

/** Right shift (rem) to re-centre the narrower modal: half the removed width. */
export const MODAL_LEFT_OFFSET_REM: number = MODAL_WIDTH_REDUCTION_REM / 2;

function getOffsetParent(element: HTMLElement) {
	const offsetParent = element.offsetParent;
	if (offsetParent) {
		return offsetParent;
	}

	// If offsetParent is null, we need to find the nearest ancestor element that has a position value other than static
	let tempOffsetParent = element.parentElement;
	while (tempOffsetParent) {
		const position = getComputedStyle(tempOffsetParent).position;
		if (position !== 'static') {
			return tempOffsetParent;
		}
		tempOffsetParent = tempOffsetParent.parentElement;
	}
	return document.body;
}

// Calculate offset for modal positioning
function getModalOffset({
	from,
	modalElement,
	direction,
}: {
	direction: 'top' | 'left';
	from: HTMLElement;
	modalElement: HTMLElement;
}) {
	const property = direction === 'top' ? 'offsetTop' : 'offsetLeft';
	let offset = from[property];
	let tempOffsetParent = from.offsetParent;
	const modalOffsetParent = getOffsetParent(modalElement);
	while (
		tempOffsetParent &&
		tempOffsetParent instanceof HTMLElement &&
		tempOffsetParent !== modalOffsetParent
	) {
		offset += tempOffsetParent[property];
		tempOffsetParent = tempOffsetParent.offsetParent;
	}
	return offset;
}

export function getModalLeft({
	relativePositionElement,
	modalElement,
}: {
	modalElement: HTMLElement;
	relativePositionElement: HTMLElement;
}): number | string {
	const offset = getModalOffset({ from: relativePositionElement, modalElement, direction: 'left' });

	// Re-centre the narrower modal only in the New AIFC experience. Gated here (not
	// in the caller) because this helper is shared with the unchanged legacy modal.
	if (isNewAifcExperienceEnabledNoExposure()) {
		return `calc(${offset}px + ${MODAL_LEFT_OFFSET_REM}rem)`;
	}

	return offset;
}

/** Reads the rendered deleted-content widgets, optionally restricted to a document range. */
type GetDeletedWidgets = (range?: { from: number; to: number }) => DeletedDiffWidget[];

/**
 * Extend `bottom` past deleted diff content that would otherwise sit behind the modal. With `smart`
 * diffs, deleted content is anchored below its replacement and can be relocated past the mount
 * point. show-diff's `getDeletedWidgets` action returns each deleted widget with the position it is
 * anchored at; only the closest one within the enclosing top-level node is measured, since anything
 * further belongs to another change and must not drag the modal further down the page.
 */
function extendBottomPastDeletedDiff({
	editorView,
	getDeletedWidgets,
	modalDecorationElement,
	modalElement,
	bottom,
}: {
	bottom: number;
	editorView: EditorView;
	getDeletedWidgets: GetDeletedWidgets;
	modalDecorationElement: HTMLElement;
	modalElement: HTMLElement;
}): number {
	let from: number;
	let to: number;
	try {
		from = editorView.posAtDOM(modalDecorationElement, 0);
		// Relocation only pushes content out of nodes that cannot host it, so it never escapes the
		// enclosing top-level node.
		const $from = editorView.state.doc.resolve(from);
		to = $from.depth > 0 ? $from.after(1) : editorView.state.doc.content.size;
	} catch {
		// posAtDOM/resolve throw when the mount point is not currently in the document.
		return bottom;
	}

	// Widgets come back ordered by position, so the first is the closest to the mount point.
	const [closest] = getDeletedWidgets({ from, to });
	if (!closest) {
		return bottom;
	}

	const elementBottom =
		getModalOffset({ from: closest.element, modalElement, direction: 'top' }) +
		closest.element.offsetHeight;
	return Math.max(bottom, elementBottom);
}

// Calculate offset top for decorationBottom positioning
function getDecorationBottom({
	editorView,
	getDeletedWidgets,
	modalDecorationElement,
	modalElement,
}: {
	editorView?: EditorView;
	getDeletedWidgets?: GetDeletedWidgets;
	modalDecorationElement: HTMLElement;
	modalElement: HTMLElement;
}) {
	const offset = getModalOffset({ from: modalDecorationElement, modalElement, direction: 'top' });
	const bottom = offset + modalDecorationElement.offsetHeight;

	// Gated on the same flag as the placement change that causes the overlap: deleted content only
	// renders after the mount point when `smart` diffs place deletions below.
	if (!editorView || !getDeletedWidgets || !fg('platform_editor_ai_smart_diff')) {
		return bottom;
	}

	return extendBottomPastDeletedDiff({
		editorView,
		getDeletedWidgets,
		modalDecorationElement,
		modalElement,
		bottom,
	});
}

export function getModalTop({
	appearance,
	modalElement,
	editorRelativeWrapper,
	modalDecorationElement,
	lastTriggeredFrom,
	editorView,
	getDeletedWidgets,
}: {
	appearance?: EditorAppearance;
	editorRelativeWrapper?: Element | null;
	editorView?: EditorView;
	getDeletedWidgets?: GetDeletedWidgets;
	lastTriggeredFrom?: InvokedFrom;
	modalDecorationElement: HTMLElement;
	modalElement: HTMLElement;
}): number {
	const isSuggestTitleQuickAction = lastTriggeredFrom === 'confluenceTitleToolbar';

	if (isSuggestTitleQuickAction) {
		return editorRelativeWrapper?.querySelector<HTMLElement>('.ProseMirror')?.offsetTop || 0;
	}
	const calculatedTop = getDecorationBottom({
		editorView,
		getDeletedWidgets,
		modalDecorationElement,
		modalElement,
	});

	// Clamp AI modal bottom within editor content area in chromeless mode
	// Calculates the bottom limit relative to the modal's offset parent
	if (appearance === 'chromeless') {
		const editorContentArea = editorView?.dom.closest<HTMLElement>('.ak-editor-content-area');
		if (editorContentArea) {
			const modalOffsetParent = getOffsetParent(modalElement);

			const contentAreaRect = editorContentArea.getBoundingClientRect();
			const modalOffsetParentRect = modalOffsetParent.getBoundingClientRect();

			const contentAreaBottom = contentAreaRect.bottom - modalOffsetParentRect.top;

			if (calculatedTop > contentAreaBottom) {
				return contentAreaBottom;
			}
		}
	}

	return calculatedTop;
}

/**
 * Gets wrapper of the nearest resizable element or the
 * wrapper of the nearest breakout element looking up from the given position
 * this wrapper is to be used as a reference point for ai modal positioning
 */
export function getResizableOrBreakoutWrapper(
	editorView: EditorView,
	position: number,
): HTMLElement | undefined {
	// For in-document streaming, the mount decoration is placed at the end of the document
	// But somehow during streaming, the position can be larger than the document size
	// So we need to clamp the position to the document size
	const resolvedPos = editorView.state.doc.resolve(
		Math.min(position, editorView.state.doc.content.size),
	);
	const { nodes, marks } = editorView.state.schema as Schema;
	const domAtPos = editorView.domAtPos.bind(editorView);

	/**
	 * At the moment, the table node is the only resizable node
	 * which can trigger the AI modal.
	 */
	const resizableNode = findParentNodeClosestToPos(
		resolvedPos,
		(currentNode) => currentNode.type === nodes.table,
	);

	if (resizableNode) {
		const resizableDomNode = findDomRefAtPos(resizableNode.start, domAtPos);

		if (resizableDomNode instanceof HTMLElement) {
			/**
			 * At the moment this only resizable tables need to be handled
			 * hence why we use the table resizer class name specifically.
			 */
			const resizableWrapper = resizableDomNode.closest(
				`.${TableSharedCssClassName.TABLE_RESIZER_CONTAINER}`,
			);
			if (resizableWrapper instanceof HTMLElement) {
				return resizableWrapper;
			}
		}
	}
	const nodeWithBreakout = findParentNodeClosestToPos(resolvedPos, (currentNode) => {
		return (
			(currentNode.type === nodes.table && currentNode.attrs.layout !== 'default') ||
			currentNode.marks.some((mark) => mark.type === marks.breakout)
		);
	});
	if (!nodeWithBreakout) {
		return;
	}
	const domNodeWithBreakout = findDomRefAtPos(nodeWithBreakout.start, domAtPos);
	if (!(domNodeWithBreakout instanceof HTMLElement)) {
		return;
	}
	if (!domNodeWithBreakout) {
		return;
	}
	if ([nodes.layoutSection, nodes.expand].includes(nodeWithBreakout.node.type)) {
		return domNodeWithBreakout.parentElement || undefined;
	}

	if (nodeWithBreakout.node.type === nodes.table) {
		const closest = domNodeWithBreakout.closest(`.${TableSharedCssClassName.TABLE_CONTAINER}`);
		return closest instanceof HTMLElement ? closest : undefined;
	}
}
